/**
 * Microsoft OAuth2 Service for handling Microsoft authentication in the frontend
 */

import axios from 'axios';
import { MICROSOFT_LOGIN, MICROSOFT_CALLBACK } from './Endpoint';

export interface MicrosoftAuthResponse {
  authorization_url: string;
  code_verifier: string;
  code_challenge: string;
  message: string;
}

export interface MicrosoftCallbackResponse {
  access_token: string;
  token_type: string;
  user: any;
  mfa_required: boolean;
}

// Microsoft OAuth2 endpoints
const MICROSOFT_TOKEN_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/token';
const MICROSOFT_USER_INFO_URL = 'https://graph.microsoft.com/v1.0/me';

class MicrosoftOAuthService {
  private codeVerifier: string | null = null;

  /**
   * Get Microsoft authorization URL and PKCE parameters from backend
   */
  async getAuthorizationUrl(): Promise<{ url: string; codeVerifier: string }> {
    try {
      const response = await axios.get<MicrosoftAuthResponse>(MICROSOFT_LOGIN);
      this.codeVerifier = response.data.code_verifier;
      return {
        url: response.data.authorization_url,
        codeVerifier: response.data.code_verifier
      };
    } catch (error) {
      console.error('Error getting Microsoft authorization URL:', error);
      throw error;
    }
  }

  /**
   * Get the Microsoft redirect URI dynamically based on current location
   * Note: window.location.origin gives us the frontend URL (e.g., http://localhost:3000)
   * This is different from process.env.REACT_APP_BASE_URL which is the backend API URL
   */
  private getRedirectUri(): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/auth/microsoft/callback`;
  }

  /**
   * Exchange authorization code for access token directly (SPA flow)
   * This must be done in the browser, not on the server
   */
  async exchangeCodeForToken(code: string, codeVerifier: string): Promise<string> {
    try {
      const response = await fetch(MICROSOFT_TOKEN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: process.env.REACT_APP_MICROSOFT_CLIENT_ID || '156ef075-694d-46d3-9c20-553bfa5da0e3',
          code: code,
          grant_type: 'authorization_code',
          redirect_uri: this.getRedirectUri(),
          code_verifier: codeVerifier,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Token exchange failed: ${errorData.error_description || errorData.error}`);
      }

      const tokenData = await response.json();
      return tokenData.access_token;
    } catch (error) {
      console.error('Error exchanging code for token:', error);
      throw error;
    }
  }

  /**
   * Handle Microsoft OAuth2 callback with access token
   * Send access token to backend for user authentication
   */
  async handleCallbackWithToken(accessToken: string): Promise<MicrosoftCallbackResponse> {
    try {
      const response = await axios.post<MicrosoftCallbackResponse>(
        MICROSOFT_CALLBACK,
        { access_token: accessToken },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error handling Microsoft callback with token:', error);
      throw error;
    }
  }

  /**
   * Initiate Microsoft login flow with PKCE
   * Opens Microsoft login in a popup window
   */
  async initiateLogin(): Promise<{ code: string; codeVerifier: string }> {
    try {
      const { url, codeVerifier } = await this.getAuthorizationUrl();
      
      // Open Microsoft login in popup
      const popup = window.open(
        url,
        'microsoft-login',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // Return a promise that resolves when the popup closes or completes
      return new Promise((resolve, reject) => {
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            reject(new Error('Login cancelled by user'));
          }
        }, 1000);

        // Listen for messages from the popup
        const messageListener = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) {
            return;
          }

          if (event.data.type === 'MICROSOFT_LOGIN_SUCCESS') {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageListener);
            popup.close();
            resolve({
              code: event.data.code,
              codeVerifier: codeVerifier
            });
          } else if (event.data.type === 'MICROSOFT_LOGIN_ERROR') {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageListener);
            popup.close();
            reject(new Error(event.data.error || 'Login failed'));
          }
        };

        window.addEventListener('message', messageListener);
      });
    } catch (error) {
      console.error('Error initiating Microsoft login:', error);
      throw error;
    }
  }

  /**
   * Complete Microsoft login flow with PKCE (SPA flow)
   * Handles the entire authentication process
   */
  async login(): Promise<MicrosoftCallbackResponse> {
    try {
      const { code, codeVerifier } = await this.initiateLogin();
      
      // Exchange code for access token directly in the browser (SPA requirement)
      const accessToken = await this.exchangeCodeForToken(code, codeVerifier);
      
      // Send access token to backend for user authentication
      const response = await this.handleCallbackWithToken(accessToken);
      return response;
    } catch (error) {
      console.error('Error in Microsoft login flow:', error);
      throw error;
    }
  }
}

export default new MicrosoftOAuthService();
